import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, Save } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'

interface ShowRow {
  id: string
  title: string
  date: string
  venue: string
  city: string
  country: string
  capacityTotal: number
  capacitySold: number
  stage: string
  tourId?: string | null
  outstanding?: { high: number; medium: number; low: number }
}

interface ShowDayTemplate {
  id: string
  name: string
  isDefault: boolean
  artistId: string | null
  _count?: { events: number }
}

export function ShowsPage() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const scope = usePerformanceScope()
  const { data, error, isLoading } = useQuery<ShowRow[], ApiClientError>({
    queryKey: ['shows', ...scope.queryKeySuffix],
    queryFn: () => artistFetch<ShowRow[]>(`/shows${scope.toQueryString()}`),
  })

  return (
    <div>
      <PageHeader
        title="Shows"
        subtitle="Click a row for the full show brief, outstanding items, and the AI show assistant."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'Add show'}
          </button>
        }
      />
      <PerformanceScopeBanner />
      {adding && (
        <AddShowForm
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['shows'] })
          }}
        />
      )}
      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No shows confirmed yet."
          columns={[
            {
              key: 'title',
              header: 'Title',
              render: (r) => (
                <Link
                  to={`/shows/${r.id}`}
                  className="font-medium text-ovation-ink hover:underline"
                >
                  {r.title}
                </Link>
              ),
            },
            {
              key: 'date',
              header: 'Date',
              render: (r) => new Date(r.date).toLocaleDateString(),
            },
            {
              key: 'venue',
              header: 'Venue',
              render: (r) => `${r.venue}, ${r.city}`,
            },
            { key: 'country', header: 'Country', render: (r) => r.country },
            {
              key: 'sold',
              header: 'Sold',
              align: 'right',
              render: (r) =>
                `${r.capacitySold.toLocaleString()} / ${r.capacityTotal.toLocaleString()}`,
            },
            {
              key: 'outstanding',
              header: 'Outstanding',
              render: (r) => {
                const o = r.outstanding
                if (!o || (o.high === 0 && o.medium === 0 && o.low === 0)) return null
                return (
                  <div className="flex items-center gap-1.5">
                    {o.high > 0 && (
                      <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
                        {o.high}
                      </span>
                    )}
                    {o.medium > 0 && (
                      <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-white">
                        {o.medium}
                      </span>
                    )}
                    {o.low > 0 && (
                      <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-sky-500 px-1.5 text-xs font-semibold text-white">
                        {o.low}
                      </span>
                    )}
                  </div>
                )
              },
            },
            {
              key: 'stage',
              header: 'Stage',
              render: (r) => (
                <span className="capitalize">{r.stage.replace(/_/g, ' ')}</span>
              ),
            },
            {
              key: 'actions',
              header: '',
              render: (r) => <SaveShowAsTemplate showId={r.id} showTitle={r.title} />,
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

/* ── Save show schedule as template ── */

function SaveShowAsTemplate({ showId, showTitle }: { showId: string; showTitle: string }) {
  const qc = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function onSave(e: FormEvent) {
    e.preventDefault()
    if (!templateName.trim()) return
    setSubmitting(true)
    try {
      // Create a new show day template and copy schedule events from this show
      await artistFetch('/show-day-templates', {
        method: 'POST',
        body: { name: templateName.trim(), cloneFromShow: showId },
      })
      setShowForm(false)
      setTemplateName('')
      qc.invalidateQueries({ queryKey: ['show-day-templates'] })
      alert('Show day template saved')
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error saving template')
    } finally {
      setSubmitting(false)
    }
  }

  if (showForm) {
    return (
      <form onSubmit={onSave} className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          required
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          className="w-32 rounded border border-ovation-ink/15 px-1.5 py-1 text-xs"
          placeholder="Template name"
          autoFocus
        />
        <button
          type="submit"
          disabled={submitting}
          className="rounded bg-ovation-ink px-1.5 py-1 text-xs text-ovation-paper disabled:opacity-50"
        >
          {submitting ? '...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setShowForm(false)}
          className="text-xs text-ovation-muted hover:text-ovation-ink"
        >
          Cancel
        </button>
      </form>
    )
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        setTemplateName(showTitle)
        setShowForm(true)
      }}
      className="flex items-center gap-1 text-xs text-ovation-muted hover:text-ovation-ink"
      title="Save show schedule as template"
    >
      <Save className="h-3.5 w-3.5" />
    </button>
  )
}

/* ── Add show form ── */

function AddShowForm({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState('')
  const [date, setDate] = useState('')
  const [showTime, setShowTime] = useState('20:00')
  const [venue, setVenue] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('United Kingdom')
  const [capacity, setCapacity] = useState(2000)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const templates = useQuery<ShowDayTemplate[], ApiClientError>({
    queryKey: ['show-day-templates'],
    queryFn: () => artistFetch<ShowDayTemplate[]>('/show-day-templates'),
  })

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      const show = await artistFetch<{ id: string }>('/shows', {
        method: 'POST',
        body: {
          title: title || `${city} — ${date}`,
          date: new Date(date).toISOString(),
          showTime: new Date(`${date}T${showTime}:00`).toISOString(),
          venue,
          city,
          country,
          capacityTotal: capacity,
        },
      })

      // If a show day template was selected, generate the schedule
      if (selectedTemplateId) {
        await artistFetch(
          `/show-day-templates/${selectedTemplateId}/generate/${show.id}`,
          { method: 'POST' },
        ).catch(() => {})
      }

      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Date</label>
          <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Show time</label>
          <input type="time" value={showTime} onChange={(e) => setShowTime(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Capacity</label>
          <input type="number" min={1} value={capacity} onChange={(e) => setCapacity(Number(e.target.value))} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Venue</label>
          <input required value={venue} onChange={(e) => setVenue(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="OVO Hydro" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">City</label>
          <input required value={city} onChange={(e) => setCity(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Glasgow" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Country</label>
          <input required value={country} onChange={(e) => setCountry(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Title (optional — auto-generates from city + date)</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Show day template (optional)</label>
        <select
          value={selectedTemplateId}
          onChange={(e) => setSelectedTemplateId(e.target.value)}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        >
          <option value="">-- no template (blank schedule) --</option>
          {(templates.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} {t.artistId === null ? '(System)' : ''} {t.isDefault ? '(Default)' : ''}
              {t._count?.events ? ` [${t._count.events} events]` : ''}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-ovation-muted">
          Selecting a template will auto-generate the show day schedule based on the show time.
        </p>
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Creating...' : selectedTemplateId ? 'Create with template' : 'Create show'}
      </button>
    </form>
  )
}
